/* ============================================================================
   FANLETIC POLISH LAYER — assets/fanletic-polish.js
   ----------------------------------------------------------------------------
   Self-initializing, dependency-free scroll-reveal driver for the polish CSS
   (assets/fanletic-polish.css). Everything else in the polish layer (card
   hover, button sweep, league-tab underline, announcement fade) is pure CSS.

   Fail-safe by construction:
   - CSS defaults are fully visible. This script only ADDS the pre-hidden
     state (html.fl-motion + .fl-reveal) after confirming BOTH that the user
     allows motion (prefers-reduced-motion) AND IntersectionObserver exists.
     If this file 404s, throws, or the checks fail, the page renders normally.
   - Reveal is once-per-element; observer disconnects when done.
   - Two recovery nets: a 3s sweep for anything still hidden inside the
     initial viewport, and a bfcache pageshow sweep that reveals everything.

   It deliberately touches NOTHING in the header: District's theme.js owns
   the menu drawer + cart count, and fanletic-predictive-search.js owns
   `header.util / #menu.panel form.search-bar input[name="q"]`. No reveal
   selector below can match inside those regions.
   ========================================================================== */
(function () {
  'use strict';

  var STAGGER_MS = 40;
  var STAGGER_CAP_MS = 280;
  var VIEWPORT_SWEEP_MS = 3000;

  /* Section inner containers + cards/tiles on the fanletic-* sections, plus
     product cards on the collection / search grids. Every selector no-ops
     gracefully when its template isn't on the page. */
  var REVEAL_SELECTORS = [
    // homepage section inners / headers
    '.fanletic-newsletter__inner',
    '.fanletic-bs__header',
    '.fanletic-league-tiles__header',
    // tiles + cards (staggered as sibling batches)
    '.fanletic-trust-bar__cell',
    '.fanletic-category-trio__card',
    '.fanletic-league-tiles__tile',
    '.fanletic-bs__grid > .fanletic-bs__item',
    // collection page (sections/fanletic-collection.liquid)
    '.fanletic-col__header',
    '.fanletic-col__grid > .fanletic-col__item',
    // search results page (templates/search.liquid)
    '.fanletic-search__header',
    '.fanletic-search__grid > .fanletic-search__item',
    // legacy District grids (old search section, list/backup collection templates)
    '.search-results .box.product',
    '.collection-container .box.product'
  ];

  function motionAllowed() {
    try {
      return !(window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false; // can't tell -> don't animate
    }
  }

  function byDomOrder(a, b) {
    /* 4 === Node.DOCUMENT_POSITION_FOLLOWING */
    return (a.compareDocumentPosition(b) & 4) ? -1 : 1;
  }

  function init() {
    if (!('IntersectionObserver' in window)) return;
    if (!motionAllowed()) return;

    var targets = [];
    for (var i = 0; i < REVEAL_SELECTORS.length; i++) {
      var found;
      try {
        found = document.querySelectorAll(REVEAL_SELECTORS[i]);
      } catch (e) {
        continue; // defensive: a bad selector must never kill the layer
      }
      for (var j = 0; j < found.length; j++) {
        if (!found[j].classList.contains('fl-reveal')) {
          found[j].classList.add('fl-reveal');
          targets.push(found[j]);
        }
      }
    }
    if (!targets.length) return;

    /* Only now arm the pre-hidden CSS state (see fanletic-polish.css §1). */
    document.documentElement.classList.add('fl-motion');

    var io = new IntersectionObserver(function (entries) {
      var batch = [];
      for (var k = 0; k < entries.length; k++) {
        if (entries[k].isIntersecting) batch.push(entries[k].target);
      }
      if (!batch.length) return;
      batch.sort(byDomOrder);
      for (var m = 0; m < batch.length; m++) {
        var delay = Math.min(m * STAGGER_MS, STAGGER_CAP_MS);
        batch[m].style.setProperty('--fl-stagger', delay + 'ms');
        batch[m].classList.add('fl-in');
        io.unobserve(batch[m]);
      }
    }, { rootMargin: '0px 0px -32px 0px', threshold: 0.06 });

    /* Above-fold content must never wait on the observer: reveal it on the
       next frame (one-frame gap keeps the entrance transition). */
    var vh0 = window.innerHeight || document.documentElement.clientHeight || 800;
    var deferred = [];
    for (var t = 0; t < targets.length; t++) {
      if (targets[t].getBoundingClientRect().top < vh0) deferred.push(targets[t]);
      else io.observe(targets[t]);
    }
    if (deferred.length) {
      requestAnimationFrame(function () {
        for (var q = 0; q < deferred.length; q++) {
          var delay = Math.min(q * STAGGER_MS, STAGGER_CAP_MS);
          deferred[q].style.setProperty('--fl-stagger', delay + 'ms');
          deferred[q].classList.add('fl-in');
        }
      });
    }

    function revealAll() {
      for (var r = 0; r < targets.length; r++) targets[r].classList.add('fl-in');
      io.disconnect();
    }

    /* Recovery net 1: nothing inside the initial viewport may stay hidden. */
    setTimeout(function () {
      var vh = window.innerHeight || document.documentElement.clientHeight || 800;
      for (var s = 0; s < targets.length; s++) {
        if (targets[s].classList.contains('fl-in')) continue;
        if (targets[s].getBoundingClientRect().top < vh) {
          targets[s].classList.add('fl-in');
          io.unobserve(targets[s]);
        }
      }
    }, VIEWPORT_SWEEP_MS);

    /* Recovery net 2: back/forward-cache restore — show everything. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) revealAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init(); // loaded with defer/async after DOM ready
  }
})();
